import { BuyBoxFilterOptions } from '@/stores/useUserStoreTypes';
import { useExperiment } from '@/libraries/experiment';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { getExperimentAffiliateTag } from '@/libraries/experiment/experiment-affiliation';
import { ObjectType } from '@/@types/graphql-types';

export type FreeTrialRowPosition = {
	/** When true, do not splice/push the standard catalogue FT row (V3: any tab except Free). */
	skipStandardInsert: boolean;
	/**
	 * 0-based index to insert the FT row when `offers.length >= insertIndex` (enough regular rows above);
	 * otherwise the FT row is appended.
	 */
	insertIndex: number;
};

export function useFreeTrialPositionExp() {
	const { activeVariantWithControlGroup: variant, experimentInfo } = useExperiment('EXP_FREE_TRIAL_POSITION');
	const { country } = storeToRefs(useLanguageStore());
	const regularOfferProviderShortNames = ['atp', 'amp', 'prv', 'hlu', 'cru'];

	function getFreeTrialRowPosition(params: { filter: BuyBoxFilterOptions }): FreeTrialRowPosition {
		const isFreeFilter = params.filter === BuyBoxFilterOptions.FREE;

		// Variant_3: hide FT row on non-Free tabs.
		if (variant.value === 'variant_3' && !isFreeFilter) {
			return { skipStandardInsert: true, insertIndex: 2 };
		}

		// V1: FT as 4th row (0-based index 3).
		if (variant.value === 'variant_1') {
			return { skipStandardInsert: false, insertIndex: 3 };
		}

		// V2: FT as 5th row (0-based index 4).
		if (variant.value === 'variant_2') {
			return { skipStandardInsert: false, insertIndex: 4 };
		}

		// Control / legacy: 3rd row (index 2) when enough regular offers.
		return { skipStandardInsert: false, insertIndex: 2 };
	}

	function allowFreeTrialPromotionUi(params: { filter: BuyBoxFilterOptions }): boolean {
		if (variant.value !== 'variant_3') return true;
		const isFreeFilter = params.filter === BuyBoxFilterOptions.FREE;
		return isFreeFilter;
	}

	function setFreeTrialPositionTags(
		url: ClickoutUrl,
		shortName: string,
		objectType: ObjectType,
		placement: 'ft' | 'ro'
	) {
		if (!variant.value) return url;
		if (placement === 'ro' && !regularOfferProviderShortNames.includes(shortName)) return url;

		const affiliationShortId = experimentInfo.value.affiliationShortId as string;
		const tag = getExperimentAffiliateTag({
			affiliationShortId,
			variant: variant.value,
			country: country.value,
			placement,
			providerShortName: shortName,
			objectType,
		});

		return url.set('uct_ctag', tag);
	}

	return {
		variant,
		experimentInfo,
		getFreeTrialRowPosition,
		allowFreeTrialPromotionUi,
		setFreeTrialPositionTags,
	};
}
